import React, { Suspense, lazy, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { LayoutGroup } from 'framer-motion';
import useIsMobile from 'hooks/useIsMobile';
import useUserData from 'hooks/useUserData';
import { setPrompt } from 'store/reducers/nlp/nlp-reducer';
import {
  currentSectionSelector,
  setCurrentSection,
} from 'store/reducers/resources/resources-reducer';
import { ThemeProvider } from 'styled-components';

import useBestOfYearStatus from '../BannerHandler/hooks/useBestOfYearStatus';
import { BestOf2025Phases } from '../BestOf2025/interfaces';
import ResourceBestOf2025MobileNav from './ResourceDetail/ResourceBestOf2025/ResourceBestOf2025MobileNav';
import { ResourcesGlobalStyles } from './ResourceDetail/styled';
import ResourcesFilterBar from './ResourcesFilterBar';
import ResourcesFilterBarBestOfEditions from './ResourcesFilterBar/ResourcesFilterBarBestOfEditions';
import ResourcesMyResources from './ResourcesMyResources';
import useResourceSubmissionHash from './hooks/useResourceSubmissionHash';
import useResourcesContent from './hooks/useResourcesContent';
import useResourcesReset from './hooks/useResourcesReset';
import { ResourcesContainer, ResourcesDetailPageContainer, ResourcesWrapper } from './styled';

const ResourcesSteps = lazy(() => import('./ResourcesSteps'));
const ResourcesDetailPage = lazy(() => import('app/pages/ResourcesDetailPage'));
const ResourcesBestOf2025 = lazy(() => import('./ResourceDetail/ResourceBestOf2025'));
const BestOfLibrary = lazy(() => import('./BestOfLibrary'));

export enum ResourcesSection {
  RESOURCES = 'resources',
  BEST_OF_2025_WINNERS = 'best_of_2025_winners',
  BEST_OF_2025_CATEGORY = 'best_of_2025_category',
  BEST_OF_2025_MY_COMPANY = 'best_of_2025_my_company',
  BEST_OF_LIBRARY = 'best_of_library',
  MY_RESOURCES = 'my_resources',
}
interface ResourcesProps {
  showDetailPage?: boolean;
  section?: ResourcesSection;
}

export default function Resources({
  showDetailPage = false,
  section = ResourcesSection.RESOURCES,
}: ResourcesProps) {
  const dispatch = useDispatch();
  const isMobile = useIsMobile();
  const userData = useUserData();
  const resourcesResetSearch = useResourcesReset();
  useResourceSubmissionHash();
  useResourcesContent();

  useEffect(() => {
    const prompt = new URLSearchParams(window.location.search).get('prompt');
    resourcesResetSearch();
    if (prompt) {
      dispatch(setPrompt(prompt));
    }
  }, []);

  return (
    <ThemeProvider
      theme={{
        allowNLP: true,
      }}
    >
      <ResourcesGlobalStyles />
      <ResourcesWrapper>
        {!isMobile ? (
          <Suspense fallback={<></>}>
            <ResourcesFilterBar />
          </Suspense>
        ) : null}
        <ResourcesContainer>
          {isMobile && !showDetailPage && section === ResourcesSection.RESOURCES ? (
            <ResourcesFilterBarBestOfEditions compact />
          ) : null}
          {showDetailPage ? (
            <Suspense fallback={<></>}>
              <ResourcesDetailPageContainer>
                <ResourcesDetailPage />
              </ResourcesDetailPageContainer>
            </Suspense>
          ) : (
            <Suspense fallback={<></>}>
              <LayoutGroup>
                {section === ResourcesSection.BEST_OF_LIBRARY ? (
                  <BestOfLibrary />
                ) : section !== ResourcesSection.MY_RESOURCES ? (
                  <ResourcesSteps />
                ) : (
                  <ResourcesMyResources />
                )}
              </LayoutGroup>
            </Suspense>
          )}
        </ResourcesContainer>
      </ResourcesWrapper>
    </ThemeProvider>
  );
}
